/* eslint-disable */
import {
  differenceInCalendarDays,
  endOfDay,
  getMonth,
  getYear,
  isAfter,
  isWithinRange,
  startOfDay,
  startOfMonth,
  subDays,
  subMonths,
} from 'date-fns'
import moment, { Moment } from 'moment'
import 'moment/locale/pl'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import DayPickerRangeController from 'react-dates/lib/components/DayPickerRangeController'
import {
  END_DATE,
  HORIZONTAL_ORIENTATION,
  INFO_POSITION_BOTTOM,
  INFO_POSITION_TOP,
  START_DATE,
  VERTICAL_ORIENTATION,
} from 'react-dates/lib/constants'
import Flex from 'typescript-styled-flex'
import { is } from '../../MediaQuery'
import { RangeType } from '../DateRangeInput'
import { isSameDate } from '../../../utils/Date'
import MuiSelect from '../MuiSelect/MuiSelect'
import phrases from './phrases'
import { MenuItem } from '@material-ui/core'
import { DateRangePickerWidgetContainer } from './styled'

function DateRangePickerWidget({
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  disabledDays,
  saveDateChanges,
  onFocusChange,
  initialFocus,
  minDate,
  maxDate,
  // It allows for proper reservation range  selecting
  // - reservation can end on day during which another reservation is starting
  allowEndOnDisabledDay = true,
  minimumNights = 1,
  allowSingleDay = false,
  withYearPicker = false,
}: IDateRangePickerWidgetProps) {
  const momentLocale = moment.locale()
  const isMobile = is.mobile()
  const normalizedMinDate = useMemo(
    () => (minDate ? startOfDay(minDate) : null),
    [minDate],
  )
  const normalizedMaxDate = useMemo(
    () => (maxDate ? endOfDay(maxDate) : null),
    [maxDate],
  )
  const [widgetRef, setWidgetRef] = useState(null)

  const [focus, setFocus] = useState(
    initialFocus === RangeType.End ? END_DATE : START_DATE,
  )
  const [isSelecting, setIsSelecting] = useState(initialFocus === RangeType.End)
  const [resetMessage, setResetMessage] = useState('')
  const initialMonth = useCallback(() => {
    let date = startDate
    if (!date) {
      date = new Date()
    }
    if (maxDate && startOfMonth(date) >= startOfMonth(maxDate)) {
      date = subMonths(maxDate, 1)
    }
    return moment(date)
  }, [startDate, maxDate])
  const isDayHighlighted = (day: Moment) => isSameDate(new Date(), day.toDate())

  const isDateRangeContainingDisabledDays = (
    startDate: Date,
    endDate: Date,
  ) => {
    const allowedEnd = allowEndOnDisabledDay ? subDays(endDate, 1) : endDate
    return (
      disabledDays &&
      !isSameDate(allowedEnd, startDate) &&
      disabledDays.some(d => isWithinRange(d, startDate, allowedEnd))
    )
  }

  const onFocus = focus => {
    setFocus(focus)
    onFocusChange &&
      onFocusChange(focus === END_DATE ? RangeType.End : RangeType.Start)
  }

  useEffect(() => {
    if (!disabledDays) return
    if (endDate) {
      // If end falls on disabled day, we have to reset range
      if (isDateRangeContainingDisabledDays(startDate, endDate)) {
        // setResetMessage(t('BBO_1438_dates_have_been_reset'))
        setIsSelecting(false)
        setEndDate(null)
        onFocus(START_DATE)
      }
    }

    if (disabledDays.some(d => isSameDate(d, startDate))) {
      setIsSelecting(false)
      onFocus(START_DATE)
    }
  }, [disabledDays, endDate])

  const isDayBlocked = (date: Moment) => {
    let jsDate = date.toDate()
    // Cannot start reservation on disabled day. Its done here, rather than in inOutsideRange, because outside range dates
    // don't have colors
    if (!isSelecting && endDate && isSameDate(jsDate, endDate)) {
      return disabledDays && disabledDays.some(d => isSameDate(d, endDate))
    }
  }

  const isOutsideRange = (date: Moment) => {
    let jsDate = date.toDate()
    if (
      (minDate && jsDate < normalizedMinDate) ||
      (maxDate && jsDate > normalizedMaxDate)
    ) {
      return true
    }
    // If is not in selecting mode, user can pick any valid start date
    if (!isSelecting) {
      return (
        disabledDays &&
        disabledDays.some(
          d =>
            isSameDate(d, jsDate) &&
            !(allowEndOnDisabledDay && isSameDate(d, endDate)),
        )
      )
    }

    // If user has selected first date, only dates which doesn't result in error are shown
    const allowedEnd = allowEndOnDisabledDay ? subDays(jsDate, 1) : jsDate
    return (
      differenceInCalendarDays(jsDate, startDate) < 0 ||
      (disabledDays &&
        disabledDays
          .filter(d => isAfter(d, startDate))
          .some(d => differenceInCalendarDays(allowedEnd, d) >= 0))
    )
  }

  const renderCalendarInfo = () =>
    resetMessage ? (
      <div style={{ padding: '10px 22px' }}>{resetMessage}</div>
    ) : null

  const onDatesChange = dates => {
    const startJsDate = startOfDay(dates.startDate.toDate())
    const endJsDate = dates.endDate ? startOfDay(dates.endDate.toDate()) : null
    setResetMessage('')
    if (focus === START_DATE) {
      setStartDate(startJsDate)
      if (setEndDate) {
        setIsSelecting(true)
        if (
          endJsDate &&
          !isDateRangeContainingDisabledDays(startJsDate, endJsDate)
        ) {
          !isMobile && saveDateChanges(startJsDate, endJsDate)
        } else {
          setEndDate(null)
        }
        onFocus(END_DATE)
      }
    } else if (endJsDate) {
      setEndDate(endJsDate)
      !isMobile && saveDateChanges(startJsDate, endJsDate)
      onFocus(START_DATE)
      setIsSelecting(false)
    }
  }

  const renderMonthElement = ({
    month,
    onMonthSelect,
    onYearSelect,
    isVisible,
  }) => {
    if (!isVisible || !widgetRef) {
      return <></>
    }
    const minYear = minDate ? getYear(minDate) : 1970
    const maxYear = maxDate ? getYear(maxDate) : 2100

    const minMonth = getMonth(minDate)
    const maxMonth = getMonth(maxDate)
    const minMonthDisplayed = month.year() === minYear ? getMonth(minDate) : 0
    const maxMonthDisplayed = month.year() === maxYear ? getMonth(maxDate) : 11

    const clampDate = (currentMonth: Moment, nextValue, year = false) => {
      let nextYear = year ? nextValue : currentMonth.year()
      let nextMonth = !year ? nextValue : currentMonth.month()
      const isFirstDisplayed =
        widgetRef && +currentMonth === +widgetRef.state.currentMonth
      const firstOffset = isMobile || isFirstDisplayed ? 0 : 1
      const secondOffset = isMobile || !isFirstDisplayed ? 0 : 1

      const stateUpdate = {
        disablePrev: false,
        disableNext: false,
      }

      if (nextYear < minYear) {
        nextYear = minYear
        nextMonth = minMonth + firstOffset
        stateUpdate.disablePrev = true
      } else if (nextYear === minYear && nextMonth <= minMonth) {
        nextMonth = minMonth + firstOffset
        stateUpdate.disablePrev = true
      }

      if (nextYear > maxYear) {
        nextYear = maxYear
        nextMonth = maxMonth - secondOffset
        stateUpdate.disableNext = true
      } else if (nextYear === maxYear && nextMonth >= maxMonth - secondOffset) {
        nextMonth = maxMonth - secondOffset
        if (nextMonth < 0) {
          nextMonth = 11 - nextMonth
          nextYear = nextYear - 1
        }
        stateUpdate.disableNext = true
      }

      widgetRef.updater.enqueueSetState(widgetRef, stateUpdate)

      currentMonth.year(nextYear)
      onMonthSelect(currentMonth, nextMonth)
    }

    const years = []
    for (let i = minYear; i <= maxYear; i++) {
      years.push(
        <MenuItem data-cy={`calendar-picker-year-${i}-select-option`} value={i}>
          {i}
        </MenuItem>,
      )
    }
    return (
      <div
        className={'yearPicker'}
        style={{ display: 'flex', justifyContent: 'center', minWidth: 0 }}
      >
        <Flex style={{ flexGrow: 1, marginRight: '10px' }}>
          <MuiSelect
            data-cy={'calendar-picker-month-select'}
            value={month.month()}
            onChange={e => clampDate(month, e.target.value)}
            style={{ width: '100%' }}
          >
            {moment.months('MMM').map((label, value) => {
              return value <= maxMonthDisplayed &&
                value >= minMonthDisplayed ? (
                <MenuItem
                  data-cy={`calendar-picker-months-${value}-select-option`}
                  value={value}
                >
                  {label}
                </MenuItem>
              ) : null
            })}
          </MuiSelect>
        </Flex>
        <Flex style={{ flexBasis: '70px' }}>
          <MuiSelect
            data-cy={'calendar-picker-years-select'}
            value={month.year()}
            onChange={e => clampDate(month, e.target.value, true)}
          >
            {years}
          </MuiSelect>
        </Flex>
      </div>
    )
  }

  return (
    <>
      <DateRangePickerWidgetContainer style={{ position: 'relative' }}>
        <DayPickerRangeController
          startDate={moment(startDate)}
          endDate={endDate ? moment(endDate) : null}
          onFocusChange={undefined}
          onDatesChange={onDatesChange}
          withPortal={isMobile}
          orientation={isMobile ? VERTICAL_ORIENTATION : HORIZONTAL_ORIENTATION}
          isOutsideRange={isOutsideRange}
          focusedInput={focus}
          numberOfMonths={isMobile ? 1 : 2}
          initialVisibleMonth={initialMonth}
          isDayHighlighted={isDayHighlighted}
          isDayBlocked={isDayBlocked}
          minimumNights={minimumNights}
          phrases={phrases}
          hideKeyboardShortcutsPanel={true}
          renderCalendarInfo={renderCalendarInfo}
          weekDayFormat={['pt', 'pt-br'].includes(momentLocale) ? 'ddd' : 'dd'} //Three letter codes look better
          calendarInfoPosition={
            isMobile ? INFO_POSITION_TOP : INFO_POSITION_BOTTOM
          }
          minDate={minDate ? moment(minDate) : null}
          maxDate={maxDate ? moment(maxDate) : null}
          noBorder={true}
          daySize={isMobile ? 35 : 39}
          transitionDuration={withYearPicker ? 0 : undefined}
          renderMonthElement={withYearPicker ? renderMonthElement : undefined}
          ref={setWidgetRef}
        />
      </DateRangePickerWidgetContainer>
    </>
  )
}

export default DateRangePickerWidget

export interface IDateRangePickerWidgetProps {
  startDate?: Date
  setStartDate?: (d: Date) => void
  endDate?: Date
  setEndDate?: (d: Date) => void
  disabledDays?: Date[]
  minDate?: Date
  maxDate?: Date
  onFocusChange?: (focus: RangeType) => void
  saveDateChanges?: (start?: Date, end?: Date) => void
  initialFocus?: RangeType
  allowEndOnDisabledDay?: boolean
  minimumNights?: number
  allowSingleDay?: boolean
  withYearPicker?: boolean
}
